import { useState, useCallback, useRef, useEffect } from 'react';
import { ApiError } from '../api/client';

/**
 * useApi - Hook for making API calls with state management
 * 
 * @param {Function} apiFunction - The API function to call
 * @param {Object} options - Configuration options
 * @returns {Object} - { data, loading, error, execute, reset }
 * 
 * @example
 * const { data, loading, error, execute } = useApi(
 *   () => apiClient.get('/users'),
 *   { immediate: true }
 * );
 */
export const useApi = (apiFunction, options = {}) => {
  const {
    immediate = false,
    initialData = null,
    onSuccess = null,
    onError = null,
  } = options;

  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState(null);
  
  // Track mounted state to prevent state updates after unmount
  const mountedRef = useRef(true);
  const apiFunctionRef = useRef(apiFunction);

  // Update ref when apiFunction changes
  useEffect(() => {
    apiFunctionRef.current = apiFunction;
  }, [apiFunction]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const execute = useCallback(async (...args) => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiFunctionRef.current(...args);
      
      if (mountedRef.current) {
        setData(result);
        setLoading(false);
        onSuccess?.(result);
      }
      
      return { data: result, error: null };
    } catch (err) {
      const apiError = err instanceof ApiError
        ? err 
        : new ApiError(err.message || 'Unknown error', 0);
      
      if (mountedRef.current) {
        setError(apiError);
        setLoading(false);
        onError?.(apiError);
      }
      
      return { data: null, error: apiError };
    }
  }, [onSuccess, onError]);

  const reset = useCallback(() => {
    setData(initialData);
    setError(null);
    setLoading(false);
  }, [initialData]);

  // Execute immediately if option is set
  useEffect(() => {
    if (immediate) {
      execute();
    }
  }, [immediate, execute]);

  return {
    data,
    loading,
    error,
    execute,
    reset,
    isError: error !== null,
    isSuccess: data !== null && error === null,
  };
};

/**
 * useLazyApi - Hook for API calls that should be triggered manually
 * Same as useApi but with immediate: false by default
 */
export const useLazyApi = (apiFunction, options = {}) => {
  return useApi(apiFunction, { ...options, immediate: false });
};

/**
 * useMutation - Hook optimized for mutations (POST, PUT, DELETE)
 * 
 * @example
 * const { mutate, loading } = useMutation(
 *   (data) => apiClient.post('/users', data)
 * );
 * 
 * const handleSubmit = () => mutate({ name: 'John' });
 */
export const useMutation = (mutationFn, options = {}) => {
  const { execute, ...rest } = useApi(mutationFn, { 
    ...options, 
    immediate: false 
  });
  
  return {
    ...rest,
    mutate: execute,
  };
};

export default useApi;
